/**
 * Project Detail — Full page with two modes:
 * 1. Chat mode (default) — project-scoped conversation
 * 2. Settings mode — full-page settings with sections
 *
 * The page shows project chat by default.
 * Settings icon navigates to settings view within the same page.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft, Settings, Plus, MessageSquare, FileText,
  Users, PanelRightClose, PanelRight, Upload, Trash2,
  Save, Library, Folder, GitBranch, Bot,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
// ADR-0033 Project Scope Phase 1 — the project page is the hub: Sources
// (real bindings + live counts + picker), Tools / Workflows / Agents
// (read-only lists), Chats unchanged. Same components, same page.
import { FolderPicker } from '@/components/library/folder-picker';
import {
  buildFolderTree,
  connectorReposToRefs,
  sourceLabel,
  sourceKindLabel,
  isUuid,
  type ConnectorWithRepos,
} from '@/components/ask-argus/project-hub.helpers';
import type {
  ProjectSourcesResponse,
  ProjectToolsResponse,
  ProjectWorkflowsResponse,
  ProjectAgentsResponse,
} from '@argusai/shared/schemas/project-scope';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  ChatMessages,
  ChatInput,
  ContextPanel,
  ChatToolsStrip,
  AttachedHighlightsChips,
  chatStreamPhaseToMessagesPhase,
} from '@/components/ask-argus';
import type { ChatSubmitContext as ComposerChatSubmitContext } from '@/components/ask-argus/chat-input';
import {
  DEFAULT_TOGGLES,
  serializeTogglesForRequest,
} from '@/components/ask-argus/chat-input.helpers';
import {
  useAttachedHighlights,
  formatHighlightsForSubmit,
} from '@/contexts/attached-highlights-context';
import { CitationVisibilityProvider } from '@/contexts/citation-visibility-context';
import { ArgusEye } from '@/components/icons';
import type { Message, Citation, PipelineStage } from '@/lib/chat-types';
// Wave 1.5 — project chat fork migrated onto the canonical hook +
// canonical Message/Citation types. The old `MockMessage`/`MockCitation`
// imports + the synchronous fake-stream handler have been removed.
import { useChatStream } from '@/lib/chat/use-chat-stream';
// ADR-0033 §8 D4 — bind / unbind now require `write:projects`. The hook
// reads the SAME matrix the API's requirePermission enforces, so a control
// hidden here is guaranteed a 403 if someone crafts the call by hand.
import { useHasPermission } from '@/lib/use-has-permission';
import { useLibraryFolders } from '@/lib/use-library-folders';
import { FolderListError } from '@/components/library/folder-picker-states';

export const Route = createFileRoute('/ask-argus/project/$projectId')({
  component: ProjectDetailPage,
  validateSearch: (search: Record<string, unknown>) => ({
    view: (search.view as string) ?? '',
    demo: search.demo === 'true' || search.demo === true,
  }),
});

// ── Project shape (backend + demo) ──────────────────────────────────────────
//
// fix-wave-4 #978 — the page now drives off `/api/v1/ai-projects/:id`. The
// canned `p1` mock that previously masqueraded as production data is gated
// behind `?demo=true` and only rendered when the param is set explicitly.

interface ProjectShape {
  name: string;
  description: string;
  color: string;
  instructions: string;
  fileCount: number;
  // bug #4 — authoritative chat count (matches the projects-list "Chats"
  // column, both derived from the backend conv_count subquery). The
  // `conversations` array below is the (capped) renderable list.
  conversationCount: number;
  members: { name: string; initials: string; role: string }[];
  conversations: { id: string; title: string; updatedAt: string; messageCount: number }[];
}

// REV-6 — the demo seed no longer carries a file list. `ProjectShape.files`
// and `DEMO_P1_FILES` were rendered nowhere, yet their count fed the Sources
// header: with `?demo=true` the sources query is disabled, so the tab read
// "0 bound sources · 4 documents readable in this project" directly above a
// "No sources bound yet" empty state. A demo project binds nothing, so its
// honest document count is 0.
const DEMO_PROJECT_DATA: Record<string, ProjectShape> = {
  p1: {
    name: 'Q2 Turnaround Planning (DEMO)',
    description: 'DEMO ONLY — All conversations related to the Q2 maintenance turnaround for Unit 3',
    color: '#8b5cf6',
    instructions: 'Focus on Unit 3 equipment. Always reference the Q2 schedule when planning work orders.',
    // A demo project binds no sources, so nothing is readable in it.
    fileCount: 0,
    conversationCount: 5,
    members: [
      { name: 'Mike Chen', initials: 'MC', role: 'Owner' },
      { name: 'Sarah Kim', initials: 'SK', role: 'Editor' },
      { name: 'John Smith', initials: 'JS', role: 'Member' },
    ],
    conversations: [
      { id: 'pc1', title: 'Compressor C-201 bearing schedule', updatedAt: '2h', messageCount: 8 },
      { id: 'pc2', title: 'Heat exchanger HX-301 inspection plan', updatedAt: '5h', messageCount: 12 },
      { id: 'pc3', title: 'Scaffolding requirements for Unit 3', updatedAt: '1d', messageCount: 5 },
      { id: 'pc4', title: 'Parts ordering timeline', updatedAt: '2d', messageCount: 15 },
      { id: 'pc5', title: 'Contractor coordination', updatedAt: '3d', messageCount: 7 },
    ],
  },
};

// Backend project DTO shape (subset of /api/v1/ai-projects/:id).
interface BackendProjectDTO {
  id: string;
  name: string;
  description: string;
  color: string;
  starred: boolean;
  conversationCount: number;
  fileCount: number;
  createdAt: string;
  createdBy: string | null;
  lastActivityAt: string;
}

// bug #4 — selecting a project conversation now loads its real message
// history (previously the non-demo path showed an empty chat). Minimal
// mirror of the main surface's dtoToMessage mapper.
interface ProjectMessageDTO {
  id: string;
  role: string;
  content: string;
  citations: unknown;
  toolCalls: unknown;
  metadata: unknown;
  createdAt: string;
  agentId?: string | null;
}
/**
 * ADR-0033 §2.2 / review finding R5 — `GET /connections-module/mcp` reports
 * `bindableReason` when an install cannot be bound to a project. The picker
 * shows the reason on the row instead of letting the bind fail into the
 * unexplained toast "Bind failed — 1 of 1 could not be bound".
 */
const MCP_NOT_BINDABLE_REASON: Record<string, string> = {
  revoked: 'revoked in Connections — cannot be bound',
  inactive: 'deactivated in Connections — cannot be bound',
  not_remote_hosted: 'platform-managed server — not bindable to a project',
};

function projectDtoToMessage(m: ProjectMessageDTO): Message {
  const meta = (m.metadata as Record<string, unknown> | null) ?? {};
  return {
    id: m.id,
    role: m.role === 'assistant' ? 'assistant' : 'user',
    content: m.content,
    agentId:
      typeof m.agentId === 'string'
        ? m.agentId
        : typeof meta.agentId === 'string'
          ? (meta.agentId as string)
          : undefined,
    citations: Array.isArray(m.citations)
      ? (m.citations as Citation[])
      : undefined,
    toolCalls: Array.isArray(m.toolCalls)
      ? (m.toolCalls as Message['toolCalls'])
      : undefined,
    // Normalize persisted 0..100 confidence back to the 0..1 scale the chip +
    // trace expect (see the dtoToMessage note on the main surface).
    confidenceScore:
      typeof meta.confidenceScore === 'number'
        ? (meta.confidenceScore as number) > 1
          ? (meta.confidenceScore as number) / 100
          : (meta.confidenceScore as number)
        : undefined,
    timestamp: new Date(m.createdAt),
    metadata: {
      model: typeof meta.model === 'string' ? (meta.model as string) : undefined,
      totalTokens:
        typeof meta.totalTokens === 'number'
          ? (meta.totalTokens as number)
          : undefined,
      latencyMs:
        typeof meta.latencyMs === 'number'
          ? (meta.latencyMs as number)
          : undefined,
    },
  };
}

// ── Component ───────────────────────────────────────────────────────────────

function ProjectDetailPage() {
  const { projectId } = Route.useParams();
  const { view: initialView, demo } = Route.useSearch();
  const navigate = useNavigate();

  // fix-wave-4 #978 — backend project fetch. The page used to hand-pick
  // the canned `p1` mock and render `Q2 Turnaround Planning` as if it
  // were live data, even on a fresh tenant with zero projects. The new
  // wiring:
  //   1. If `?demo=true` is set AND the id matches a DEMO_PROJECT_DATA
  //      key, render the seed (useful for screenshot capture). The demo
  //      banner makes it obvious this is not real data.
  //   2. Otherwise fetch /api/v1/ai-projects/:id. While loading we show
  //      a spinner. On 404 / non-found we show the existing "Project
  //      not found" empty state. On success we render real data, and
  //      any sub-tabs that depend on still-missing endpoints (files,
  //      members, conversations) render explicit "coming soon" stubs.
  const demoProject =
    demo && DEMO_PROJECT_DATA[projectId] ? DEMO_PROJECT_DATA[projectId] : null;

  const projectQuery = useQuery<BackendProjectDTO>({
    queryKey: ['ai-project', projectId],
    queryFn: () => apiClient.get(`/ai-projects/${projectId}`),
    enabled: !demoProject,
    retry: false,
  });

  // bug #4 — the project's conversations. Previously hardcoded to `[]`,
  // which made the Overview read "0 Conversations" and the in-project list
  // empty even when chats were attached. This fetches the real chats
  // (folder_id = projectId) so the list + count are consistent with the
  // projects-list "Chats" column.
  const projectConvosQuery = useQuery<{
    data: Array<{
      id: string;
      title: string;
      messageCount: number;
      lastMessageAt: string | null;
      updatedAt: string;
    }>;
    total: number;
  }>({
    queryKey: ['ai-project-conversations', projectId],
    queryFn: () => apiClient.get(`/ai-projects/${projectId}/conversations`),
    enabled: !demoProject,
    retry: false,
  });

  // Map backend DTO -> page ProjectShape. Files + members come from
  // yet-unwired endpoints so default to empty arrays; the relevant
  // sub-tabs render explicit "coming soon" copy when empty. Conversations
  // are now real (bug #4).
  const project: ProjectShape | null = (() => {
    if (demoProject) return demoProject;
    if (!projectQuery.data) return null;
    const p = projectQuery.data;
    const convos = (projectConvosQuery.data?.data ?? []).map((c) => ({
      id: c.id,
      title: c.title,
      updatedAt: c.lastMessageAt ?? c.updatedAt,
      messageCount: c.messageCount ?? 0,
    }));
    return {
      name: p.name,
      description: p.description ?? '',
      color: p.color ?? '#8b5cf6',
      // The backend project row does not carry a per-project system
      // prompt today; leave instructions empty until that endpoint
      // lands. Sub-tabs render explicit placeholders rather than
      // pretending the field is wired.
      instructions: '',
      fileCount: p.fileCount ?? 0,
      conversationCount: p.conversationCount ?? convos.length,
      members: [],
      conversations: convos,
    };
  })();

  // ── Hooks (must run unconditionally on every render) ────────────────────
  // BUGFIX #29 (React error #310 "Rendered more hooks than during the
  // previous render"): the loading-spinner and "Project not found" early
  // returns used to sit ABOVE these hooks. On the first render the query
  // was still loading → early return → ZERO hooks ran. On the next render
  // the query resolved → all the hooks below ran → hook count changed →
  // React threw #310 and the whole page crashed. The fix is the rules-of-
  // hooks invariant: declare EVERY hook first, unconditionally, then do the
  // conditional early returns AFTER all hooks (see below, just before the
  // settings-view render).
  const [view, setView] = useState<'chat' | 'settings'>(initialView === 'settings' ? 'settings' : 'chat');
  const [settingsTab, setSettingsTab] = useState<'overview' | 'instructions' | 'tools' | 'sources' | 'team' | 'workflows' | 'agents'>('overview');
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  // Wave 1.5 — canonical Message type from chat-types.ts (no more
  // MockMessage). Drives ChatMessages + ContextPanel just like the
  // main Ask Argus surface.
  const [messages, setMessages] = useState<Message[]>([]);
  const [rightOpen, setRightOpen] = useState(false);
  const { highlights, clear: clearHighlights } = useAttachedHighlights();
  const queryClient = useQueryClient();

  // #4 — real project deletion. The danger-zone button previously only fired
  // `toast.error('Project deleted (mock)')` with no backend call (status-truth
  // lie). This calls DELETE /ai-projects/:id, then invalidates the list and
  // navigates back to the projects index.
  const deleteProject = useMutation<void, Error, void>({
    mutationFn: () => apiClient.delete(`/ai-projects/${projectId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ai-projects'] });
      toast.success('Project deleted');
      void navigate({ to: '/ask-argus/projects' });
    },
    onError: (e) => toast.error('Delete failed', { description: e.message }),
  });

  // Settings form state. BUGFIX #29 — these initialize from `project`,
  // which is null while the query is in flight (the hooks now run BEFORE
  // the not-found/loading early returns). Seed with safe defaults and sync
  // from `project` once it resolves via the effect below.
  const [editName, setEditName] = useState(project?.name ?? '');
  const [editDescription, setEditDescription] = useState(project?.description ?? '');
  const [editInstructions, setEditInstructions] = useState(project?.instructions ?? '');
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
  // ADR-0033 §2.2 Phase 2 (T14) — the tool picker, one dialog for both kinds.
  // null = closed; the kind decides which tenant list it offers.
  const [toolPickerKind, setToolPickerKind] = useState<'mcp' | 'api' | null>(null);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  // ── ADR-0033 Project Scope — hub data (Sources / Tools / Workflows / Agents)
  //
  // Sources are the project's bound Library folders, connector repos and
  // documents (project_sources); the header count is the de-duplicated
  // readable-document set the retrieval lens sees. Tools / Workflows /
  // Agents are read-only in Phase 1 (binding UI + enforcement = Phase 2/4).
  const sourcesQuery = useQuery<ProjectSourcesResponse>({
    queryKey: ['ai-project-sources', projectId],
    queryFn: () => apiClient.get(`/ai-projects/${projectId}/sources`),
    enabled: !demoProject,
    retry: false,
  });
  const toolsQuery = useQuery<ProjectToolsResponse>({
    queryKey: ['ai-project-tools', projectId],
    queryFn: () => apiClient.get(`/ai-projects/${projectId}/tools`),
    enabled: !demoProject && view === 'settings' && settingsTab === 'tools',
    retry: false,
  });
  const workflowsQuery = useQuery<ProjectWorkflowsResponse>({
    queryKey: ['ai-project-workflows', projectId],
    queryFn: () => apiClient.get(`/ai-projects/${projectId}/workflows`),
    enabled: !demoProject && view === 'settings' && settingsTab === 'workflows',
    retry: false,
  });
  const agentsQuery = useQuery<ProjectAgentsResponse>({
    queryKey: ['ai-project-agents', projectId],
    queryFn: () => apiClient.get(`/ai-projects/${projectId}/agents`),
    enabled: !demoProject && view === 'settings' && settingsTab === 'agents',
    retry: false,
  });

  // Tool-picker inputs — the TENANT's connections, from the same endpoints the
  // Connections rail and the chat tools strip already read. Fetched only while
  // the picker is open, and only for the kind being picked.
  // `bindable` / `bindableReason` (review findings R5 + BF-4) come from the same
  // predicate the bind endpoint enforces, so the picker below can stop offering
  // rows that `POST /ai-projects/:id/tools/mcp` will refuse with a bare 400.
  const tenantMcpQuery = useQuery<{ servers: Array<{ id: string; displayName: string; toolCount: number; status: string; bindable?: boolean; bindableReason?: string }> }>({
    queryKey: ['connections-module', 'mcp'],
    queryFn: () => apiClient.get('/connections-module/mcp'),
    enabled: !demoProject && toolPickerKind === 'mcp',
    retry: false,
  });
  const tenantApiQuery = useQuery<{ connections: Array<{ id: string; displayName: string; endpointCount: number; status: string }> }>({
    queryKey: ['connections-module', 'api'],
    queryFn: () => apiClient.get('/connections-module/api'),
    enabled: !demoProject && toolPickerKind === 'api',
    retry: false,
  });
  const boundMcpIds = useMemo(
    () => new Set((toolsQuery.data?.mcp ?? []).map((m) => m.tenantMcpInstallId)),
    [toolsQuery.data],
  );
  const boundApiIds = useMemo(
    () => new Set((toolsQuery.data?.api ?? []).map((a) => a.tenantApiConnectionId)),
    [toolsQuery.data],
  );

  // Picker inputs: Library folders (tree) + GitHub connector repos. The
  // connectors LIST omits `config`; `selectedRepos` lives on the detail
  // route, so the list is fetched then each github connector's detail.
  // Decision 18 — the one folder list (lib/use-library-folders.ts), the same
  // folders, with the same ids, as the Library sidebar (L18-06).
  const foldersQuery = useLibraryFolders({ enabled: !demoProject && libraryPickerOpen });
  const connectorsQuery = useQuery<ConnectorWithRepos[]>({
    queryKey: ['ai-project-picker-connectors'],
    queryFn: async () => {
      const list = await apiClient.get<{ data: Array<{ id: string; displayName: string; connectorKind: string }> }>(
        '/library/connectors?kind=github',
      );
      return Promise.all(
        (list.data ?? []).map((c) =>
          apiClient
            .get<{ config: Record<string, unknown> }>(`/library/connectors/${c.id}`)
            .then((d) => ({ ...c, config: d.config as ConnectorWithRepos['config'] }))
            .catch(() => ({ ...c, config: null })),
        ),
      );
    },
    enabled: !demoProject && libraryPickerOpen,
  });
  const [pickedFolders, setPickedFolders] = useState<Set<string>>(() => new Set());
  const [pickedRepos, setPickedRepos] = useState<Set<string>>(() => new Set());
  /** T14 — install ids / connection ids ticked in the tool picker. */
  const [pickedTools, setPickedTools] = useState<Set<string>>(() => new Set());
  const folderTree = useMemo(() => buildFolderTree(foldersQuery.data ?? []), [foldersQuery.data]);
  const repoOptions = useMemo(() => connectorReposToRefs(connectorsQuery.data ?? []), [connectorsQuery.data]);
  const boundRefs = useMemo(
    () => new Set((sourcesQuery.data?.sources ?? []).map((s) => `${s.sourceKind}:${s.sourceRef}`)),
    [sourcesQuery.data],
  );

  const invalidateProjectScope = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['ai-project-sources', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['ai-project', projectId] });
    void queryClient.invalidateQueries({ queryKey: ['ai-projects'] });
  }, [queryClient, projectId]);

  // ADR-0033 §8 D4 — one gate for every source-binding affordance on this
  // page (Select-from-Library, the picker's Bind button, the per-row unbind).
  // Affordance only: the API is the barrier.
  const canManageSources = useHasPermission('write', 'projects');
  // P2 review REV-1 / BF-1 — the danger-zone "Delete project" control was the
  // one write affordance D4 left visible to every role, while being strictly
  // more destructive than the unbind it hid. DELETE /ai-projects/:id now
  // requires `delete:projects`; this is the matching affordance gate.
  const canDeleteProject = useHasPermission('delete', 'projects');

  // Bind: one POST /ai-projects/:id/sources per new selection. Partial
  // success is reported honestly and the lists are refreshed either way.
  const bindSources = useMutation<
    void,
    Error,
    Array<{ sourceKind: 'folder' | 'connector_repo'; sourceRef: string }>
  >({
    mutationFn: async (selection) => {
      const results = await Promise.allSettled(
        selection.map((s) => apiClient.post(`/ai-projects/${projectId}/sources`, s)),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        throw new Error(`${failed.length} of ${selection.length} source${selection.length === 1 ? '' : 's'} could not be bound`);
      }
    },
    onSuccess: (_d, selection) => {
      toast.success(`Bound ${selection.length} source${selection.length === 1 ? '' : 's'} to this project`);
      invalidateProjectScope();
      setPickedFolders(new Set());
      setPickedRepos(new Set());
      setLibraryPickerOpen(false);
    },
    onError: (e) => {
      toast.error('Bind failed', { description: e.message });
      invalidateProjectScope();
    },
  });
  const unbindSource = useMutation<void, Error, { id: string; label: string }>({
    mutationFn: ({ id }) => apiClient.delete(`/ai-projects/${projectId}/sources/${id}`),
    onSuccess: (_d, { label }) => {
      toast.success(`Removed ${label} from this project`);
      invalidateProjectScope();
    },
    onError: (e) => toast.error('Remove failed', { description: e.message }),
  });

  // ── ADR-0033 §2.2 Phase 2 (T14) — tool bind / unbind ─────────────────────
  //
  // Same shape as bindSources/unbindSource above: one request per selection,
  // partial success reported honestly, the list refetched either way so the
  // counts on screen are the API's and never local state.
  const invalidateProjectTools = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['ai-project-tools', projectId] });
  }, [queryClient, projectId]);

  const bindTools = useMutation<
    void,
    Error,
    { kind: 'mcp' | 'api'; ids: string[] }
  >({
    mutationFn: async ({ kind, ids }) => {
      const results = await Promise.allSettled(
        ids.map((id) =>
          apiClient.post(
            `/ai-projects/${projectId}/tools/${kind}`,
            kind === 'mcp' ? { tenantMcpInstallId: id } : { tenantApiConnectionId: id },
          ),
        ),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        // R5 — carry the server's reason through. A bare count left the user
        // unable to tell "this install was revoked" from "the request broke".
        const reasons = [
          ...new Set(
            failed.map((r) => {
              const e = (r as PromiseRejectedResult).reason as { message?: string };
              return (e?.message ?? '').trim();
            }).filter(Boolean),
          ),
        ];
        throw new Error(
          `${failed.length} of ${ids.length} could not be bound${reasons.length ? `: ${reasons.join('; ')}` : ''}`,
        );
      }
    },
    onSuccess: (_d, { kind, ids }) => {
      toast.success(
        `Bound ${ids.length} ${kind === 'mcp' ? 'MCP server' : 'API connection'}${ids.length === 1 ? '' : 's'} to this project`,
      );
      invalidateProjectTools();
      setPickedTools(new Set());
      setToolPickerKind(null);
    },
    onError: (e) => {
      toast.error('Bind failed', { description: e.message });
      invalidateProjectTools();
    },
  });

  const unbindTool = useMutation<void, Error, { kind: 'mcp' | 'api'; id: string; label: string }>({
    mutationFn: ({ kind, id }) => apiClient.delete(`/ai-projects/${projectId}/tools/${kind}/${id}`),
    onSuccess: (_d, { label }) => {
      toast.success(`Removed ${label} from this project`);
      invalidateProjectTools();
    },
    onError: (e) => toast.error('Remove failed', { description: e.message }),
  });

  // Wave 1.5 — wire the real SSE consumer for non-demo project chat.
  // Demo mode still injects canned messages on submit (see
  // `handleSubmit` below) because there is no demo backend.
  const chatStream = useChatStream({
    endpoint: '/ecosystem/query',
    authToken: () => {
      try {
        return localStorage.getItem('accessToken');
      } catch {
        return null;
      }
    },
    onAgentBubble: ({ bubbleId, bubbleKey, agentId, displayName, kind, retryPrompt }) => {
      setMessages((prev) => [
        ...prev,
        {
          id: bubbleId,
          role: 'assistant',
          content: '',
          agentId: bubbleKey === '__default__' ? undefined : agentId,
          agentDisplayName: displayName,
          agentKind: kind,
          timestamp: new Date(),
          retryPrompt,
        },
      ]);
    },
    onMetadata: ({ bubbleId, model, intent, displayName, kind }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId
            ? {
                ...m,
                agentDisplayName: displayName ?? m.agentDisplayName,
                agentKind: kind ?? m.agentKind,
                metadata: {
                  ...(m.metadata ?? {}),
                  model: model ?? m.metadata?.model,
                  intent: intent ?? m.metadata?.intent,
                },
              }
            : m,
        ),
      );
    },
    onToken: ({ bubbleId, fullContent }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId ? { ...m, content: fullContent } : m,
        ),
      );
    },
    onCitation: ({ bubbleId, citations }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId
            ? { ...m, citations: citations.length > 0 ? citations : undefined }
            : m,
        ),
      );
    },
    onPipeline: ({ bubbleId, stages }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId ? { ...m, pipeline: stages as PipelineStage[] } : m,
        ),
      );
    },
    onConfidence: ({ bubbleId, confidence, rationale }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId
            ? {
                ...m,
                confidenceScore: confidence,
                confidenceRationale: rationale,
              }
            : m,
        ),
      );
    },
    onError: ({ bubbleId, error, timedOut }) => {
      if (timedOut) {
        toast.error('Response timed out — server stopped sending updates');
      } else if (error.code?.startsWith('HTTP_')) {
        toast.error(`Ask Argus error (${error.code.replace('HTTP_', '')})`);
      } else if (error.code === 'SSE_RECONNECT_FAILED') {
        toast.error('Lost connection to Ask Argus');
      } else {
        toast.error(error.message);
      }
      setMessages((prev) =>
        prev.map((m) => (m.id === bubbleId ? { ...m, error } : m)),
      );
    },
    onDone: ({ bubbleId, totalTokens, latencyMs }) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === bubbleId
            ? {
                ...m,
                metadata: {
                  ...(m.metadata ?? {}),
                  totalTokens: totalTokens ?? m.metadata?.totalTokens,
                  latencyMs: latencyMs ?? m.metadata?.latencyMs,
                },
              }
            : m,
        ),
      );
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
    onAbort: ({ bubbleId }) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== bubbleId) return m;
          const existing = m.content;
          return {
            ...m,
            content:
              existing + (existing ? '\n\n' : '') + '_Generation stopped._',
          };
        }),
      );
    },
  });
  const {
    isStreaming,
    phase: chatStreamPhase,
    activePipelinePhaseLabel,
    send: chatSend,
    abort: chatAbort,
  } = chatStream;
  const streamingPhase = chatStreamPhaseToMessagesPhase(chatStreamPhase);

  const handleSelectConversation = useCallback((id: string) => {
    // fix-wave-4 #978 — DEMO mode keeps the canned "mock conversation"
    // assistant bubble so screenshot capture still has something to show.
    // bug #4 — real (non-demo) project chats now load their actual message
    // history from /conversations/:id/messages (previously the chat opened
    // empty, reinforcing the "chats not visible" complaint).
    setActiveConvId(id);
    if (demoProject) {
      setMessages([
        {
          id: 'pu1',
          role: 'user',
          content: 'Mock conversation in project (DEMO).',
          timestamp: new Date(Date.now() - 3600000),
        },
        {
          id: 'pa1',
          role: 'assistant',
          content: `[DEMO] This conversation is scoped to **${project?.name ?? ''}**.\n\nProject instructions: "${project?.instructions ?? ''}"\n\nAll ${project?.fileCount ?? 0} knowledge files are available.`,
          metadata: { model: 'qwen2.5:7b', totalTokens: 64, latencyMs: 1200 },
          confidenceScore: 0.95,
          timestamp: new Date(Date.now() - 3500000),
        },
      ]);
    } else {
      setMessages([]);
      // Load the real history. Fire-and-forget; only apply if this is still
      // the selected conversation when the fetch resolves.
      apiClient
        .get<{ data: ProjectMessageDTO[] }>(`/conversations/${id}/messages`)
        .then((res) => {
          const loaded = (res.data ?? []).map(projectDtoToMessage);
          setActiveConvId((cur) => {
            if (cur === id) setMessages(loaded);
            return cur;
          });
        })
        .catch(() => {
          /* leave the empty-state copy in place on failure */
        });
    }
    setRightOpen(true);
  }, [project, demoProject]);

  const handleNewChat = useCallback(() => {
    // ADR-0033 — no client-local placeholder id: null means "create on first
    // send" (the tenant-wide page's pattern), so the stream always carries a
    // real conversation UUID the route accepts.
    setActiveConvId(null);
    setMessages([]);
  }, []);

  /**
   * Wave 1.5 — project chat now uses the canonical `useChatStream`
   * hook for real (non-demo) sends. Demo mode (`?demo=true`) still
   * appends canned mock bubbles synchronously because there is no
   * demo backend — that path is reserved for screenshot capture.
   *
   * The previous behavior of appending a hardcoded "Project-scoped
   * chat is not yet wired" placeholder is GONE. Non-demo project
   * chats route through `/ecosystem/query` just like the main Ask
   * Argus surface.
   */
  /**
   * The composer's live selection, republished by ChatInput on every change.
   * Fallback for turns that don't originate in the composer so they honour the
   * toolbar instead of reverting to DEFAULT_TOGGLES.
   */
  const composerContextRef = useRef<ComposerChatSubmitContext | null>(null);
  const handleComposerContextChange = useCallback(
    (next: ComposerChatSubmitContext) => {
      composerContextRef.current = next;
    },
    [],
  );

  const handleSubmit = useCallback(
    async (text: string, ctx?: ComposerChatSubmitContext) => {
      if (isStreaming) return;
      const body = formatHighlightsForSubmit(highlights, text);
      if (highlights.length > 0) clearHighlights();

      // Optimistic user echo (shared across both modes).
      const userMsg: Message = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: body,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);

      if (demoProject) {
        // Demo path — keep the canned response so screenshot capture
        // still works. NO server roundtrip.
        const assistantMsg: Message = {
          id: `a-${Date.now()}`,
          role: 'assistant',
          content: `[DEMO — Project: ${project?.name ?? ''}] Mock response to: "${text}"`,
          metadata: { model: 'qwen2.5:7b', totalTokens: 40, latencyMs: 1000 },
          confidenceScore: 0.9,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, assistantMsg]);
        return;
      }

      // Same defect as the main Ask Argus route: turns started from a
      // follow-up chip / Regenerate / Retry / Edit / Continue carry no submit
      // context, so this silently reverted the user's Library-Only / Web /
      // Thinking selections to defaults. Fall back to the composer's live
      // state, which ChatInput republishes on every change.
      const effectiveCtx = ctx ?? composerContextRef.current ?? undefined;
      const toggles = serializeTogglesForRequest(
        effectiveCtx?.toggles ?? DEFAULT_TOGGLES,
      );

      // ADR-0033 (founder amendment 3) — create on first send. The route's
      // body schema is z.string().uuid() for conversationId, so the old
      // client-local placeholder was rejected on the very first in-project
      // send. Mirror the tenant-wide page: POST /conversations, then attach
      // it to the project through the existing tenant-validated
      // PUT /conversations/:id/project (so conversations.folder_id — the
      // persisted project link — agrees with the request projectId), then
      // stream with the real UUID.
      let convId = isUuid(activeConvId) ? activeConvId : null;
      if (!convId) {
        try {
          const created = await apiClient.post<{ id: string }>('/conversations', {
            title: text.slice(0, 80),
          });
          await apiClient.put(`/conversations/${created.id}/project`, { projectId });
          convId = created.id;
          setActiveConvId(created.id);
          void queryClient.invalidateQueries({ queryKey: ['ai-project-conversations', projectId] });
        } catch (e) {
          toast.error('Could not start a conversation in this project', {
            description: e instanceof Error ? e.message : undefined,
          });
          return;
        }
      }

      await chatSend(body, {
        conversationId: convId,
        agentId: effectiveCtx?.agent || undefined,
        brand: effectiveCtx?.model || undefined,
        toggles,
        extraBody: { projectId },
      });
    },
    [
      project,
      projectId,
      activeConvId,
      highlights,
      clearHighlights,
      demoProject,
      isStreaming,
      chatSend,
      queryClient,
    ],
  );

  // ADR-0033 — the live readable-document count, viewer-scoped server-side
  // (PSP1-09: ACL + clearance, so it matches what retrieval reaches).
  //
  // REV-6 — in demo mode `sourcesQuery` is disabled and the demo project binds
  // nothing, so this is 0: the Sources header and the empty-chat line now
  // agree with the "No sources bound yet" state instead of contradicting it.
  const totalDocuments = demoProject ? 0 : (sourcesQuery.data?.totalDocuments ?? project?.fileCount ?? 0);
  const sources = sourcesQuery.data?.sources ?? [];

  /**
   * Wave 1.5 — citations on the right panel now scope to ONLY the
   * most-recent assistant message (founder fix already applied on the
   * main Ask Argus surface; previously this fork still concatenated
   * every citation from every message in the conversation).
   */
  const latestAssistant = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return messages[i];
    }
    return undefined;
  }, [messages]);
  const allCitations: Citation[] = latestAssistant?.citations ?? [];
  const hasMessages = messages.length > 0;
  const handleStopGeneration = useCallback(() => {
    chatAbort();
  }, [chatAbort]);

  // BUGFIX #29 — sync the settings-form fields from `project` once the
  // query resolves (the initial useState seeds ran while project was still
  // null). Keyed on the project's identity so it refreshes on navigation
  // between projects. Settings save has no backend yet, so there are no
  // user edits to clobber.
  useEffect(() => {
    if (project) {
      setEditName(project.name);
      setEditDescription(project.description);
      setEditInstructions(project.instructions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, project?.name, project?.description, project?.instructions]);

  // ── Conditional early returns (AFTER all hooks — see BUGFIX #29) ─────────
  if (!demoProject && projectQuery.isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-8">
        <p className="text-sm text-muted-foreground">Loading project…</p>
      </div>
    );
  }

  // Handle invalid project ID (404 / not-found / non-demo unknown id)
  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-8">
        <h2 className="text-lg font-semibold mb-2">Project not found</h2>
        <p className="text-sm text-muted-foreground mb-4">The project you're looking for doesn't exist or you don't have access.</p>
        <Button onClick={() => navigate({ to: '/ask-argus/projects' })}>Back to Projects</Button>
      </div>
    );
  }

  // ── Settings View ──────────────────────────────────────────────────────
  if (view === 'settings') {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        {/* Header + tabs */}
        <div className="border-b border-border/40 bg-background shrink-0">
          <div className="max-w-4xl mx-auto px-6">
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setView('chat')} aria-label="Back to chat" title="Back">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: project.color }} />
                  <h1 className="text-lg font-semibold">{project.name}</h1>
                </div>
              </div>
              {/*
                Cluster C-STUB-TOAST (Pod-S, 2026-05-15) — settings save
                has no backend (PATCH /ai-projects/:id does not exist).
                Per Phase 2 resolved decision #3 the button must be
                visibly disabled with a "Coming soon" tooltip rather
                than fire a misleading green toast. The onClick still
                returns to /chat view so the operator can leave the
                Settings panel; no DB write happens.
              */}
              <Button
                className="gap-2"
                disabled
                title="Coming soon — settings persistence is not yet wired"
                onClick={() => { setView('chat'); }}
              >
                <Save className="h-4 w-4" /> Save
              </Button>
            </div>

            {/* Horizontal tabs */}
            <div className="flex items-center gap-0.5">
              {([
                { key: 'overview' as const, label: 'Overview', icon: MessageSquare },
                { key: 'instructions' as const, label: 'Instructions', icon: FileText },
                { key: 'tools' as const, label: 'Tools', icon: Settings },
                // ADR-0033 — 'Knowledge' relabelled 'Sources'; Workflows +
                // Agents appended. Same strip, same styling.
                { key: 'sources' as const, label: 'Sources', icon: Library },
                { key: 'team' as const, label: 'Team', icon: Users },
                { key: 'workflows' as const, label: 'Workflows', icon: GitBranch },
                { key: 'agents' as const, label: 'Agents', icon: Bot },
              ]).map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setSettingsTab(tab.key)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors',
                    settingsTab === tab.key
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  )}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-auto">
          <div className="max-w-4xl mx-auto px-6 py-6">

            {/* Overview */}
            {settingsTab === 'overview' && (
              <div className="space-y-6">
                <div className="grid grid-cols-4 gap-4">
                  <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-semibold">{project.conversationCount}</p><p className="text-xs text-muted-foreground">Conversations</p></CardContent></Card>
                  <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-semibold">{totalDocuments}</p><p className="text-xs text-muted-foreground">Documents</p></CardContent></Card>
                  <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-semibold">{project.members.length}</p><p className="text-xs text-muted-foreground">Members</p></CardContent></Card>
                  {/* bug #4 — Connectors per-project is not a wired feature; the
                      old hardcoded "3/5" was a fabricated stat. Show the real
                      count (0 until per-project connectors land) rather than lie. */}
                  <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-semibold">0</p><p className="text-xs text-muted-foreground">Connectors</p></CardContent></Card>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Project name</Label>
                    <Input value={editName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditName(e.target.value)} />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea value={editDescription} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditDescription(e.target.value)} rows={3} />
                  </div>
                </div>

                {canDeleteProject && (
                <Card className="border-destructive/30">
                  <CardContent className="py-4 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Delete this project</p>
                      <p className="text-xs text-muted-foreground">Permanently delete the project. Its conversations are kept and moved out of the project.</p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive" size="sm" disabled={deleteProject.isPending}>
                          {deleteProject.isPending ? 'Deleting…' : 'Delete project'}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this project?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This permanently deletes “{project?.name ?? 'this project'}”. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => deleteProject.mutate()}
                          >
                            Delete project
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </CardContent>
                </Card>
                )}
              </div>
            )}

            {/* Instructions */}
            {settingsTab === 'instructions' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-base font-semibold mb-1">Custom Instructions</h2>
                  <p className="text-sm text-muted-foreground mb-4">These instructions are prepended to every conversation in this project. They shape how the AI responds.</p>
                </div>
                <Textarea value={editInstructions} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setEditInstructions(e.target.value)} rows={12} className="font-mono text-sm" placeholder="Instructions that apply to all conversations..." />
              </div>
            )}

            {/* Tools — ADR-0033 §2.2: bound MCP servers + API connections. Phase 2 (T14) adds bind/unbind here; the tool lens enforces it in chat. */}
            {settingsTab === 'tools' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-base font-semibold mb-1">Tools</h2>
                  {/* R2 / BF-3 — say only what is enforced. The MCP lens is
                      real (tool catalogue + gateway); nothing at runtime calls
                      an API connection yet, so claiming the API bindings narrow
                      anything would be a promise the runtime does not keep. */}
                  <p className="text-sm text-muted-foreground mb-4">MCP servers and API connections bound to this project. Inside this project the effective MCP tool set is what an agent is granted, narrowed to the MCP servers bound here.</p>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-semibold">MCP servers</h3>
                    {/* ADR-0033 §8 D4 — binding requires write:projects. */}
                    {canManageSources && (
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { setPickedTools(new Set()); setToolPickerKind('mcp'); }} disabled={!!demoProject}>
                        <Plus className="h-3.5 w-3.5" /> Add
                      </Button>
                    )}
                  </div>
                  {(toolsQuery.data?.mcp ?? []).length === 0 ? (
                    <p className="text-xs text-muted-foreground py-3">
                      {toolsQuery.isLoading ? 'Loading…' : 'No MCP servers bound to this project yet.'}
                    </p>
                  ) : (
                    <div className="border rounded-lg divide-y divide-border/40">
                      {(toolsQuery.data?.mcp ?? []).map((m) => (
                        <div key={m.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                          <p className="text-sm font-medium truncate flex-1 min-w-0">{m.displayName}</p>
                          <Badge variant="outline" className="text-[10px] shrink-0">{m.toolCount} tool{m.toolCount === 1 ? '' : 's'}</Badge>
                          {/* ADR-0033 §8 D4 — unbinding requires write:projects. */}
                          {canManageSources && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive shrink-0"
                              disabled={unbindTool.isPending}
                              aria-label={`Unbind '${m.displayName}'`}
                              title="Remove this MCP server from the project"
                              onClick={() => unbindTool.mutate({ kind: 'mcp', id: m.id, label: m.displayName })}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3
                      className="text-sm font-semibold"
                      // R2 / BF-3 — the binding is recorded, RBAC-gated and
                      // audited, but no runtime path calls an API connection
                      // yet, so it narrows nothing today. Said here rather than
                      // implied by the section's presence.
                      title="Recorded for this project. API connections are not callable as tools yet, so these bindings do not narrow anything at runtime."
                    >
                      API connections
                    </h3>
                    {canManageSources && (
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { setPickedTools(new Set()); setToolPickerKind('api'); }} disabled={!!demoProject}>
                        <Plus className="h-3.5 w-3.5" /> Add
                      </Button>
                    )}
                  </div>
                  {(toolsQuery.data?.api ?? []).length === 0 ? (
                    <p className="text-xs text-muted-foreground py-3">
                      {toolsQuery.isLoading ? 'Loading…' : 'No API connections bound to this project yet.'}
                    </p>
                  ) : (
                    <div className="border rounded-lg divide-y divide-border/40">
                      {(toolsQuery.data?.api ?? []).map((a) => (
                        <div key={a.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                          <p className="text-sm font-medium truncate flex-1 min-w-0">{a.displayName}</p>
                          <Badge variant="outline" className="text-[10px] shrink-0">{a.endpointCount} endpoint{a.endpointCount === 1 ? '' : 's'}</Badge>
                          {canManageSources && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive shrink-0"
                              disabled={unbindTool.isPending}
                              aria-label={`Unbind '${a.displayName}'`}
                              title="Remove this API connection from the project"
                              onClick={() => unbindTool.mutate({ kind: 'api', id: a.id, label: a.displayName })}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {toolsQuery.data && toolsQuery.data.mcp.length === 0 && toolsQuery.data.api.length === 0 && (
                  <div className="text-center py-6 text-muted-foreground">
                    <Settings className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No tools bound to this project yet</p>
                    <p className="text-xs mt-1">Manage tenant-wide connections in <a href="/connections" className="underline">Connections</a>.</p>
                  </div>
                )}
              </div>
            )}

            {/* Sources — ADR-0033: bound Library folders / connector repos / documents with live counts */}
            {settingsTab === 'sources' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold">Sources</h2>
                    <p className="text-sm text-muted-foreground">
                      {sources.length} bound source{sources.length === 1 ? '' : 's'} · {totalDocuments} document{totalDocuments === 1 ? '' : 's'} readable in this project
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* ADR-0033 §8 D4 — binding requires write:projects. */}
                    {canManageSources && (
                      <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setLibraryPickerOpen(true)} disabled={!!demoProject}>
                        <Library className="h-3.5 w-3.5" /> Select from Library
                      </Button>
                    )}
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setUploadOpen(true)}>
                      <Upload className="h-3.5 w-3.5" /> Upload new
                    </Button>
                  </div>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Source</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Kind</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Documents</th>
                        <th className="w-10 px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {sources.map((s) => (
                        <tr key={s.id} className="hover:bg-muted/30">
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              {s.sourceKind === 'folder' ? (
                                <Folder className="h-4 w-4 text-muted-foreground shrink-0" />
                              ) : s.sourceKind === 'connector_repo' ? (
                                <GitBranch className="h-4 w-4 text-muted-foreground shrink-0" />
                              ) : (
                                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                              )}
                              <span className="truncate">{sourceLabel(s)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5">
                            <Badge variant="outline" className="text-[9px]">{sourceKindLabel(s.sourceKind)}</Badge>
                          </td>
                          <td className="px-4 py-2.5 text-muted-foreground tabular-nums">{s.documentCount}</td>
                          <td className="px-2 py-2.5">
                            {/* ADR-0033 §8 D4 — unbinding requires write:projects. */}
                            {canManageSources && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-destructive"
                                disabled={unbindSource.isPending}
                                aria-label={`Unbind '${sourceLabel(s)}'`}
                                title="Remove this source from the project"
                                onClick={() => unbindSource.mutate({ id: s.id, label: sourceLabel(s) })}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {sources.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Library className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">{sourcesQuery.isLoading ? 'Loading sources…' : 'No sources bound yet'}</p>
                    <p className="text-xs mt-1">Bind Library folders or connector repositories; inside this project, retrieval sees only what is bound.</p>
                  </div>
                )}
              </div>
            )}

            {/* Workflows — ADR-0033: read-only (schema landed; runner enforcement is a later phase) */}
            {settingsTab === 'workflows' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-base font-semibold">Workflows</h2>
                  <p className="text-sm text-muted-foreground">Workflows whose default project is this one, with their scope mode and last run.</p>
                </div>
                {(workflowsQuery.data?.workflows ?? []).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <GitBranch className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">{workflowsQuery.isLoading ? 'Loading…' : 'No workflows run in this project yet'}</p>
                  </div>
                ) : (
                  <div className="border rounded-lg divide-y divide-border/40">
                    {(workflowsQuery.data?.workflows ?? []).map((w) => (
                      <div key={w.id} className="flex items-center justify-between px-4 py-2.5 gap-3">
                        <p className="text-sm font-medium truncate flex-1 min-w-0">{w.name}</p>
                        <Badge variant="outline" className="text-[10px]">{w.status}</Badge>
                        <Badge variant="outline" className="text-[10px]">{w.mode === 'narrowed' ? 'Narrowed' : 'Inherits project scope'}</Badge>
                        <span className="text-[11px] text-muted-foreground shrink-0 tabular-nums">
                          {w.lastRunAt ? `Last run ${new Date(w.lastRunAt).toLocaleString()}` : 'Never run'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Agents — ADR-0033: read-only "ran here" list */}
            {settingsTab === 'agents' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-base font-semibold">Agents</h2>
                  <p className="text-sm text-muted-foreground">Agents that have run inside this project's conversations. Agents live at tenant level and run inside a project.</p>
                </div>
                {(agentsQuery.data?.agents ?? []).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Bot className="h-8 w-8 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">{agentsQuery.isLoading ? 'Loading…' : 'No agents have run in this project yet'}</p>
                  </div>
                ) : (
                  <div className="border rounded-lg divide-y divide-border/40">
                    {(agentsQuery.data?.agents ?? []).map((a) => (
                      <div key={a.agentId} className="flex items-center justify-between px-4 py-2.5 gap-3">
                        <p className="text-sm font-medium truncate flex-1 min-w-0">{a.displayName ?? a.agentId}</p>
                        <Badge variant="outline" className="text-[10px]">{a.runCount} run{a.runCount === 1 ? '' : 's'}</Badge>
                        <span className="text-[11px] text-muted-foreground shrink-0 tabular-nums">
                          {a.lastRunAt ? `Last run ${new Date(a.lastRunAt).toLocaleString()}` : 'Never run'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Team */}
            {settingsTab === 'team' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold">Team Members</h2>
                    <p className="text-sm text-muted-foreground">{project.members.length} members</p>
                  </div>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setAddMemberOpen(true)}><Plus className="h-3.5 w-3.5" /> Add member</Button>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Member</th>
                        <th className="text-left px-4 py-2 font-medium text-muted-foreground">Role</th>
                        <th className="w-10 px-2 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {project.members.map((m, i) => (
                        <tr key={i} className="hover:bg-muted/30">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">{m.initials}</div>
                              <p className="text-sm font-medium">{m.name}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3"><Badge variant="outline" className="text-[10px]">{m.role}</Badge></td>
                          <td className="px-2 py-3">{m.role !== 'Owner' && (
                            /*
                              Cluster C-STUB-TOAST (Pod-S) — DELETE
                              /ai-projects/:id/members/:uid does not yet
                              exist. Disabled with Coming-soon tooltip
                              per Phase 2 resolved decision #3.
                            */
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive"
                              disabled
                              aria-label={`Remove member '${m.name}'`}
                              title="Coming soon — member removal endpoint not yet wired"
                            ><Trash2 className="h-3.5 w-3.5" /></Button>
                          )}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Library Picker Dialog — ADR-0033: Library folders (FolderPicker tree) + connector repos → POST /ai-projects/:id/sources */}
        <Dialog
          open={libraryPickerOpen}
          onOpenChange={(open) => {
            setLibraryPickerOpen(open);
            if (!open) {
              setPickedFolders(new Set());
              setPickedRepos(new Set());
            }
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Select from Library</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Library folders</p>
                {foldersQuery.isLoading ? (
                  <p className="text-xs text-muted-foreground py-3">Loading folders…</p>
                ) : foldersQuery.isError && !foldersQuery.data ? (
                  // LB2-T3-R3 — a failed read is an error, never an empty tree.
                  <FolderListError
                    consequence="No Library folder can be added until it loads."
                    onRetry={() => void foldersQuery.refetch()}
                  />
                ) : (
                  <FolderPicker
                    tree={folderTree}
                    selected={pickedFolders}
                    onToggle={(folderId) =>
                      setPickedFolders((prev) => {
                        const next = new Set(prev);
                        if (next.has(folderId)) next.delete(folderId);
                        else next.add(folderId);
                        return next;
                      })
                    }
                  />
                )}
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Connector repositories</p>
                {connectorsQuery.isLoading ? (
                  <p className="text-xs text-muted-foreground py-3">Loading connectors…</p>
                ) : repoOptions.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-3">No GitHub connectors with selected repositories.</p>
                ) : (
                  <div className="space-y-1 max-h-40 overflow-y-auto border rounded-lg p-2">
                    {repoOptions.map((r) => {
                      const alreadyBound = boundRefs.has(`connector_repo:${r.ref}`);
                      return (
                        <label key={r.ref} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
                          <Checkbox
                            checked={alreadyBound || pickedRepos.has(r.ref)}
                            disabled={alreadyBound}
                            onCheckedChange={() =>
                              setPickedRepos((prev) => {
                                const next = new Set(prev);
                                if (next.has(r.ref)) next.delete(r.ref);
                                else next.add(r.ref);
                                return next;
                              })
                            }
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate">{r.ownerRepo}</p>
                            <p className="text-[10px] text-muted-foreground">{r.connectorName}{alreadyBound ? ' · already bound' : ''}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLibraryPickerOpen(false)}>Cancel</Button>
              {/* ADR-0033 §8 D4 — the picker's commit button is the write. */}
              <Button
                disabled={
                  !canManageSources ||
                  bindSources.isPending ||
                  pickedFolders.size + pickedRepos.size === 0
                }
                onClick={() =>
                  bindSources.mutate([
                    ...[...pickedFolders]
                      .filter((id) => !boundRefs.has(`folder:${id}`))
                      .map((id) => ({ sourceKind: 'folder' as const, sourceRef: id })),
                    ...[...pickedRepos]
                      .filter((ref) => !boundRefs.has(`connector_repo:${ref}`))
                      .map((ref) => ({ sourceKind: 'connector_repo' as const, sourceRef: ref })),
                  ])
                }
              >
                {bindSources.isPending ? 'Binding…' : `Bind ${pickedFolders.size + pickedRepos.size} selected`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tool Picker Dialog — ADR-0033 §2.2 (T14): the tenant's MCP servers /
            API connections → POST /ai-projects/:id/tools/{mcp,api}. One dialog,
            same Checkbox + DialogFooter shape as the Library picker above. */}
        <Dialog
          open={toolPickerKind !== null}
          onOpenChange={(open) => {
            if (!open) {
              setToolPickerKind(null);
              setPickedTools(new Set());
            }
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {toolPickerKind === 'api' ? 'Add API connections' : 'Add MCP servers'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                {toolPickerKind === 'api'
                  ? 'API connections registered in Connections. Binding one scopes it to this project.'
                  : 'MCP servers installed for this tenant in Connections. Binding one scopes it to this project.'}
              </p>
              {(() => {
                const loading = toolPickerKind === 'api' ? tenantApiQuery.isLoading : tenantMcpQuery.isLoading;
                const options =
                  toolPickerKind === 'api'
                    ? (tenantApiQuery.data?.connections ?? []).map((c) => ({
                        id: c.id,
                        name: c.displayName,
                        detail: `${c.endpointCount} endpoint${c.endpointCount === 1 ? '' : 's'}`,
                        bound: boundApiIds.has(c.id),
                        blocked: null as string | null,
                      }))
                    : (tenantMcpQuery.data?.servers ?? []).map((s) => ({
                        id: s.id,
                        name: s.displayName,
                        detail: `${s.toolCount} tool${s.toolCount === 1 ? '' : 's'}`,
                        bound: boundMcpIds.has(s.id),
                        // R5 + BF-4 — the server says whether the bind endpoint
                        // will take it. Older API builds omit the field; treat
                        // an absent flag as bindable so this degrades to the
                        // previous behaviour rather than emptying the picker.
                        blocked:
                          s.bindable === false
                            ? MCP_NOT_BINDABLE_REASON[s.bindableReason ?? ''] ??
                              'not available to bind'
                            : null,
                      }));
                if (loading) return <p className="text-xs text-muted-foreground py-3">Loading…</p>;
                if (options.length === 0) {
                  return (
                    <p className="text-xs text-muted-foreground py-3">
                      Nothing to bind yet. Add {toolPickerKind === 'api' ? 'an API connection' : 'an MCP server'} in{' '}
                      <a href="/connections" className="underline">Connections</a> first.
                    </p>
                  );
                }
                return (
                  <div className="space-y-1 max-h-60 overflow-y-auto border rounded-lg p-2">
                    {options.map((o) => (
                      <label key={o.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/50 cursor-pointer transition-colors">
                        <Checkbox
                          checked={(o.bound || pickedTools.has(o.id)) && !o.blocked}
                          disabled={o.bound || !!o.blocked}
                          onCheckedChange={() =>
                            setPickedTools((prev) => {
                              const next = new Set(prev);
                              if (next.has(o.id)) next.delete(o.id);
                              else next.add(o.id);
                              return next;
                            })
                          }
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{o.name}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {o.detail}
                            {o.bound ? ' · already bound' : ''}
                            {/* R5 — say WHY, instead of letting the bind fail
                                into an unexplained "1 of 1 could not be bound". */}
                            {o.blocked ? ` · ${o.blocked}` : ''}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setToolPickerKind(null); setPickedTools(new Set()); }}>Cancel</Button>
              {/* ADR-0033 §8 D4 — the picker's commit button is the write. */}
              <Button
                disabled={!canManageSources || bindTools.isPending || pickedTools.size === 0}
                onClick={() =>
                  toolPickerKind &&
                  bindTools.mutate({ kind: toolPickerKind, ids: [...pickedTools] })
                }
              >
                {bindTools.isPending ? 'Binding…' : `Bind ${pickedTools.size} selected`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Upload Dialog */}
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload files</DialogTitle>
            </DialogHeader>
            <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/30 transition-colors cursor-pointer">
              <Upload className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm font-medium">Drop files here or click to browse</p>
              <p className="text-xs text-muted-foreground mt-1">PDF, DOCX, XLSX, CSV, TXT, Markdown</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
              {/* Cluster C-STUB-TOAST (Pod-S) — no upload endpoint yet. */}
              <Button
                disabled
                title="Coming soon — project file upload endpoint not yet wired"
              >Upload</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Member Dialog */}
        <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Add team member</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-2">
                <Label>Email or name</Label>
                <Input placeholder="Search by name or email..." />
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground mb-2">Suggested</p>
                {[
                  { name: 'Lisa Park', initials: 'LP', email: 'lisa.park@acme.com' },
                  { name: 'David Lee', initials: 'DL', email: 'david.lee@acme.com' },
                ].map(person => (
                  <button key={person.email} className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted/50 transition-colors text-left">
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-semibold text-primary">{person.initials}</div>
                    <div className="min-w-0">
                      <p className="text-sm">{person.name}</p>
                      <p className="text-[10px] text-muted-foreground">{person.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAddMemberOpen(false)}>Cancel</Button>
              {/* Cluster C-STUB-TOAST (Pod-S) — no POST /ai-projects/:id/members endpoint yet. */}
              <Button
                disabled
                title="Coming soon — project membership endpoint not yet wired"
              >Add</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ── Chat View ──────────────────────────────────────────────────────────
  return (
    // PR-AAU-B — wrap project chat with the same citation-visibility
    // provider as the main Ask Argus surface so the composer's
    // Citations [ON/OFF] toggle has somewhere to write through.
    <CitationVisibilityProvider>
    <div className="flex h-full overflow-hidden">
      {/* Left: Project Sidebar */}
      <div className="w-[280px] shrink-0 border-r border-border/40 flex flex-col h-full overflow-hidden">
        <div className="p-3 shrink-0">
          <Button variant="ghost" size="sm" className="gap-1.5 text-muted-foreground mb-2 -ml-1" onClick={() => navigate({ to: '/ask-argus/projects' })}>
            <ArrowLeft className="h-3.5 w-3.5" /> Projects
          </Button>

          <div className="flex items-center gap-2 mb-1">
            <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: project.color }} />
            <h2 className="font-semibold text-sm truncate">{project.name}</h2>
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2 mb-2">{project.description}</p>

          <div className="flex items-center gap-3 text-[11px] text-muted-foreground mb-3">
            <span className="flex items-center gap-1"><Users className="h-3 w-3" /> {project.members.length}</span>
            <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {totalDocuments} documents</span>
          </div>

          <div className="flex items-center gap-1.5">
            <Button size="sm" className="flex-1 gap-1.5" onClick={handleNewChat}>
              <Plus className="h-3.5 w-3.5" /> New chat
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8 shrink-0" onClick={() => setView('settings')} aria-label="Project settings" title="Settings">
              <Settings className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <Separator />

        <div className="flex-1 overflow-y-auto p-2">
          <p className="text-[11px] font-medium text-muted-foreground px-2 pt-2 pb-1">Conversations</p>
          {project.conversations.map(conv => (
            <div
              key={conv.id}
              onClick={() => handleSelectConversation(conv.id)}
              className={cn(
                'rounded-lg cursor-pointer transition-colors px-2 py-2',
                activeConvId === conv.id ? 'bg-accent text-accent-foreground' : 'hover:bg-muted/50 text-foreground'
              )}
            >
              <div className="flex items-center gap-1.5">
                <p className="flex-1 min-w-0 truncate text-sm">{conv.title}</p>
                <span className="text-[10px] text-foreground/40 shrink-0 tabular-nums">{conv.updatedAt}</span>
                {/* Per-conversation actions (share/download/rename/pin/clone/archive/delete)
                    were no-op toasts that echoed the action and changed nothing — removed
                    rather than shown as working. Re-add when the conversation-action
                    endpoints are wired. The row stays clickable to open the conversation. */}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Center: Chat */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Tools strip with project name + panel toggle — single row */}
        <ChatToolsStrip
          rightAction={
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px] text-muted-foreground gap-1 shrink-0"
              onClick={() => setRightOpen(!rightOpen)}
            >
              {rightOpen ? <PanelRightClose className="h-3.5 w-3.5" /> : <PanelRight className="h-3.5 w-3.5" />}
              <span>{rightOpen ? 'Hide' : 'Panel'}</span>
            </Button>
          }
        />

        {hasMessages ? (
          <>
            <ChatMessages
              messages={messages}
              isStreaming={isStreaming}
              streamingPhase={streamingPhase}
              streamingLabel={streamingPhase === 'thinking' ? activePipelinePhaseLabel : undefined}
            />
            <AttachedHighlightsChips />
            <ChatInput
              onContextChange={handleComposerContextChange}
              onSubmit={handleSubmit}
              disabled={isStreaming}
              isStreaming={isStreaming}
              onStop={handleStopGeneration}
            />
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center px-8">
            <ArgusEye state="idle" className="w-12 h-12 mb-4" />
            <h2 className="text-lg font-semibold mb-1">Start a conversation</h2>
            <p className="text-sm text-muted-foreground text-center max-w-md mb-4">
              This conversation has access to {totalDocuments} document{totalDocuments === 1 ? '' : 's'} bound to this project.
            </p>
            <AttachedHighlightsChips />
            <ChatInput
              onContextChange={handleComposerContextChange}
              onSubmit={handleSubmit}
              placeholder={`Ask about ${project.name}...`}
              disabled={isStreaming}
              isStreaming={isStreaming}
              onStop={handleStopGeneration}
            />
          </div>
        )}
      </div>

      {/* Right: Context Panel */}
      <div className={cn('shrink-0 border-l border-border/40 overflow-hidden transition-all duration-200', rightOpen ? 'w-[300px]' : 'w-0')}>
        {rightOpen && <ContextPanel citations={allCitations} />}
      </div>
    </div>
    </CitationVisibilityProvider>
  );
}
